import { useState, useEffect } from 'react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { KeyRound, CheckCircle, AlertCircle } from 'lucide-react';
import StaticBlob from '../utils/StaticBlob';

function ResetPasswordScreen({ apiUrl }) {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Extract token from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');

    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsResetting(true);

    try {
      const response = await fetchWithRetry(`${apiUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setNewPassword('');
        setConfirmPassword('');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-paper-bg)] flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-500">
      {/* Animated background */}
      <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none overflow-hidden">
        <StaticBlob color="var(--color-accent-warm)" tintAlpha={0.35} className="absolute top-20 left-10 w-72 h-72" />
        <StaticBlob color="var(--color-ink-light)" tintAlpha={0.35} className="absolute bottom-20 right-10 w-96 h-96" />
      </div>

      <div className="glass-morphism rounded-3xl shadow-2xl p-10 w-full max-w-md relative z-10 bg-[var(--color-paper-warm)] border border-[var(--color-ink-subtle)]/10 animate-slideUp" style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}>
        <div className="text-center mb-10">
          <div className="inline-flex p-4 rounded-3xl mb-6">
            <img
              src={process.env.PUBLIC_URL + "/android-chrome-192x192.png"}
              alt="Steadily.me logo"
              className="w-16 h-16 opacity-90 contrast-125 filter sepia-[.25]"
            />
          </div>
          <h1 className="text-5xl font-bold font-serif text-[var(--color-ink-main)] mb-3">
            Steadily.me
          </h1>
          <p className="text-[var(--color-ink-light)] text-lg font-serif italic">Reset Your Password</p>
        </div>

        {success ? (
          <div className="text-center space-y-4">
            <CheckCircle className="w-16 h-16 mx-auto text-green-600" />
            <div className="text-green-700 text-lg bg-green-500/10 border border-green-500/20 rounded-xl p-4">
              {success}
            </div>
            <p className="text-[var(--color-ink-light)] text-sm font-serif">
              Redirecting to login...
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <KeyRound className="w-12 h-12 mx-auto text-[var(--color-accent-warm)] mb-3" />
              <p className="text-[var(--color-ink-light)] text-sm font-serif">
                Enter your new password below
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="relative group">
                <input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-5 py-4 bg-transparent text-[var(--color-ink-main)] border-2 border-[var(--color-ink-subtle)]/30 focus:border-[var(--color-ink-main)] focus:outline-none transition-all placeholder-[var(--color-ink-subtle)]/50 font-medium"
                  style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
                  required
                  disabled={!token || isResetting}
                />
              </div>

              <div className="relative group">
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-5 py-4 bg-transparent text-[var(--color-ink-main)] border-2 border-[var(--color-ink-subtle)]/30 focus:border-[var(--color-ink-main)] focus:outline-none transition-all placeholder-[var(--color-ink-subtle)]/50 font-medium"
                  style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
                  required
                  disabled={!token || isResetting}
                />
              </div>

              {error && (
                <div className="flex items-start space-x-2 text-red-600 text-sm bg-red-50/50 border-l-4 border-red-500 p-3 rounded-lg font-medium">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={!token || isResetting}
                className="w-full py-4 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] font-bold font-serif text-lg hover:bg-[var(--color-ink-light)] transition-all transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
              >
                {isResetting ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <a
                href="/"
                className="text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors font-medium font-serif italic border-b border-transparent hover:border-[var(--color-ink-main)]"
              >
                ← Back to sign in
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordScreen;
